import {useCallback, useEffect, useRef, useState} from 'react';

const WAKE_WORDS = new Set(['dynamics', 'dynamix', 'dynamic', 'dynamique']);

const heardWakeWord = value => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .split(/[^a-z]+/)
  .some(word => WAKE_WORDS.has(word));

const wakeCommand = value => {
  const text = String(value || '').trim();
  const match = text.match(/\b(?:dynamics|dynamix|dynamic|dynamique)\b/i);
  return match ? text.slice(match.index + match[0].length).replace(/^[\s,;:!?.-]+/, '').trim() : '';
};

export default function useDynamicsWakeWord({active, onWake, userId}) {
  const storageKey = userId ? `dynamics-wake-word:${userId}` : null;
  const supported = typeof window !== 'undefined' && Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);
  const [enabled, setEnabled] = useState(() => {
    if (!storageKey) return false;
    try { return localStorage.getItem(storageKey) === 'true'; } catch { return false; }
  });
  const [listening, setListening] = useState(false);
  const [error, setError] = useState('');
  const recognitionRef = useRef(null);
  const retryRef = useRef(null);
  const wakeTimerRef = useRef(null);
  const loadedStorageKeyRef = useRef(null);
  const stateRef = useRef({active, enabled, onWake});
  stateRef.current = {active, enabled, onWake};

  useEffect(() => {
    if (!storageKey || loadedStorageKeyRef.current === storageKey) return;
    loadedStorageKeyRef.current = storageKey;
    try { setEnabled(localStorage.getItem(storageKey) === 'true'); }
    catch { setEnabled(false); }
  }, [storageKey]);

  const stop = useCallback(() => {
    clearTimeout(wakeTimerRef.current);
    clearTimeout(retryRef.current);
    retryRef.current = null;
    const recognition = recognitionRef.current;
    recognitionRef.current = null;
    if (recognition) {
      recognition.onend = null;
      recognition.onstart = null;
      recognition.onerror = null;
      recognition.onresult = null;
      try { recognition.stop(); } catch {}
    }
    setListening(false);
  }, []);

  const startRef = useRef(null);
  const start = useCallback(() => {
    if (!supported || recognitionRef.current || !stateRef.current.active || !stateRef.current.enabled) return;
    const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new Recognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'fr-FR';
    recognitionRef.current = recognition;
    recognition.onstart = () => { setListening(true); setError(''); };
    recognition.onresult = event => {
      for (let index = event.resultIndex || 0; index < event.results.length; index += 1) {
        const result = event.results[index];
        const transcript = result?.[0]?.transcript;
        if (!heardWakeWord(transcript)) continue;
        clearTimeout(wakeTimerRef.current);
        const wake = () => {
          if(recognitionRef.current!==recognition)return;
          stop();
          stateRef.current.onWake?.({transcript, command:wakeCommand(transcript)});
        };
        // Some browsers never finalize a short wake word. Wait for a stable
        // partial result while allowing the rest of the command to arrive.
        if(result?.isFinal===false)wakeTimerRef.current=setTimeout(wake,900);
        else wake();
        break;
      }
    };
    recognition.onerror = event => {
      if (event.error === 'aborted') return;
      const denied = ['not-allowed','service-not-allowed'].includes(event.error);
      setError(denied ? 'Autorisez le microphone pour activer le réveil vocal.' : 'Le réveil vocal est momentanément indisponible.');
      setListening(false);
      recognitionRef.current = null;
      if (denied) {
        recognition.onend = null;
      } else if (stateRef.current.active && stateRef.current.enabled) {
        clearTimeout(retryRef.current);
        retryRef.current = setTimeout(() => startRef.current?.(), 500);
      }
    };
    recognition.onend = () => {
      recognitionRef.current = null;
      setListening(false);
      if (stateRef.current.active && stateRef.current.enabled) {
        clearTimeout(retryRef.current);
        retryRef.current = setTimeout(() => startRef.current?.(), 500);
      }
    };
    try { recognition.start(); }
    catch { recognitionRef.current = null; setError('Le réveil vocal n’a pas pu démarrer.'); }
  }, [stop, supported]);
  startRef.current = start;

  useEffect(() => {
    if (active && enabled) start(); else stop();
    return stop;
  }, [active, enabled, start, stop]);

  const toggle = async () => {
    if (!supported) return;
    const next = !enabled;
    setError('');
    if (next && navigator.mediaDevices?.getUserMedia) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({audio:true});
        stream.getTracks().forEach(track => track.stop());
      } catch {
        setError('Autorisez le microphone pour activer le réveil vocal.');
        return;
      }
    }
    setEnabled(next);
    try { if (storageKey) localStorage.setItem(storageKey, String(next)); } catch {}
  };

  return {supported, enabled, listening, error, toggle};
}

export {heardWakeWord, wakeCommand};
